import { privateClient } from "@/api/privateClient";
import { INSTITUTION_ADMIN_ENDPOINTS } from "../../institutionAdmin.endpoints";
import type {
  FeeStructureStatus,
  CollectionCounts,
  FormerStudentDues,
  CollectionRow,
  CollectionSort,
  CollectionStatus,
  AssignmentPreview,
  FeeDashboard,
  FeeCalendar,
  FeeSchedule,
  AutoAssignResult,
  FeeComponent,
  FeeStructure,
  FeeStructureItemInput,
  StudentFeeInvoice,
  FeePayment,
  FeesOverviewReport,
  FeesBreakdownRow,
  GenerateBulkResult,
  PaymentMode,
  InvoiceDiscountsResult,
  DiscountType,
  AdhocFeeCharge,
  AdhocFeeChargeResult,
  AdhocChargeTargetScope,
  BankAccount,
  BankAccountType,
  ConcessionTemplate,
  ConcessionType,
  ConcessionValueType,
  StudentConcession,
  FullYearQuote,
  FeePolicy,
  FeeHead,
} from "../types";

const FEES = INSTITUTION_ADMIN_ENDPOINTS.FEES;

export async function fetchFeeComponents(institutionId: number, params?: { scope?: "adhoc" }): Promise<FeeComponent[]> {
  const { data } = await privateClient.get(FEES.COMPONENTS.LIST(institutionId), { params });
  return data.components ?? [];
}

export async function createFeeComponent(
  institutionId: number,
  payload: { name: string; facilityTag?: string; description?: string; isAdhocEligible?: boolean }
): Promise<FeeComponent> {
  const { data } = await privateClient.post(FEES.COMPONENTS.CREATE(institutionId), payload);
  return data.component;
}

export async function updateFeeComponent(
  institutionId: number,
  componentId: number,
  payload: { name?: string; description?: string; isActive?: boolean; isAdhocEligible?: boolean; collectAt?: "ADMISSION" | "LATER"; billingKind?: BillingKind; chargedTo?: "ALL" | "NEW_ADMISSION" | "RE_ADMISSION"; termNos?: number[] | null }
): Promise<FeeComponent> {
  const { data } = await privateClient.patch(FEES.COMPONENTS.UPDATE(institutionId, componentId), payload);
  return data.component;
}

export async function deleteFeeComponent(
  institutionId: number,
  componentId: number
): Promise<{ deleted?: boolean; component?: FeeComponent; deactivatedInstead?: boolean }> {
  const { data } = await privateClient.delete(FEES.COMPONENTS.DELETE(institutionId, componentId));
  return data;
}

export async function fetchFeeStructures(
  institutionId: number,
  params?: { academicYearId?: number; academicGroupId?: number }
): Promise<FeeStructure[]> {
  const { data } = await privateClient.get(FEES.STRUCTURES.LIST(institutionId), { params });
  return data.structures ?? [];
}

export async function fetchFeeStructure(institutionId: number, structureId: number): Promise<FeeStructure> {
  const { data } = await privateClient.get(FEES.STRUCTURES.GET(institutionId, structureId));
  return data.structure;
}

export async function saveFeeStructure(
  institutionId: number,
  payload: { academicYearId: number; academicGroupId: number; name?: string; items: FeeStructureItemInput[]; inheritsParent?: boolean }
): Promise<FeeStructure> {
  // Check if structure already exists
  const existing = await fetchFeeStructures(institutionId, {
    academicYearId: payload.academicYearId,
    academicGroupId: payload.academicGroupId,
  });

  if (existing.length > 0) {
    // UPDATE existing structure
    const { data } = await privateClient.put(
      FEES.STRUCTURES.UPDATE(institutionId, existing[0].id),
      payload
    );
    return data.structure;
  }

  // CREATE new structure
  const { data } = await privateClient.post(FEES.STRUCTURES.CREATE(institutionId), payload);
  return data.structure;
}

export interface BulkStructureEntry {
  academicGroupId: number;
  /** The structure's updatedAt as last seen, or null for a class with none; a mismatch means somebody else changed it. */
  expectedUpdatedAt: string | null;
  items?: FeeStructureItemInput[];
  remove?: boolean;
  inheritsParent?: boolean;
}

/** Saves several classes together, all or none. A rejected save carries `code` STRUCTURES_CHANGED (with `groups`) or STRUCTURES_INVALID (with `failures`). */
export async function saveFeeStructures(
  institutionId: number,
  payload: { academicYearId: number; structures: BulkStructureEntry[] }
): Promise<{ structures: { academicGroupId: number; id: number; status: FeeStructureStatus }[]; removed: number[] }> {
  const { data } = await privateClient.put(`${feesBase(institutionId)}/structures-bulk`, payload);
  return data;
}

export async function deleteFeeStructure(institutionId: number, structureId: number): Promise<void> {
  await privateClient.delete(FEES.STRUCTURES.DELETE(institutionId, structureId));
}

export async function publishFeeStructure(
  institutionId: number,
  structureId: number
): Promise<{ structure: FeeStructure; autoAssigned: AutoAssignResult | null }> {
  const { data } = await privateClient.put(FEES.STRUCTURES.UPDATE(institutionId, structureId), {
    status: "PUBLISHED",
  });
  return { structure: data.structure, autoAssigned: data.autoAssigned ?? null };
}

/** Read-only "what will publishing do" — students reached, schedule and per-student total. */
export async function fetchAssignmentPreview(institutionId: number, structureId: number): Promise<AssignmentPreview> {
  const { data } = await privateClient.get(`${FEES.STRUCTURES.GET(institutionId, structureId)}/assignment-preview`);
  return data;
}

export async function generateInvoiceForStudent(
  institutionId: number,
  payload: { studentId: number; academicYearId: number }
): Promise<StudentFeeInvoice> {
  const { data } = await privateClient.post(FEES.INVOICES.GENERATE(institutionId), payload);
  return data.invoice;
}

/** Drops + rebuilds the student's invoice from the current published structure (fails if it has payments). */
export async function regenerateInvoiceForStudent(
  institutionId: number,
  payload: { studentId: number; academicYearId: number; chargeFullYear?: boolean; startDate?: string | null }
): Promise<StudentFeeInvoice> {
  const { data } = await privateClient.post(FEES.INVOICES.REGENERATE(institutionId), payload);
  return data.invoice;
}

export async function fetchFeeSchedules(institutionId: number, academicYearId: number): Promise<FeeCalendar> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/schedules`, { params: { academicYearId } });
  return data;
}

export async function suggestFeeSchedules(institutionId: number, academicYearId: number): Promise<{ fromYear: { id: number; label: string }; schedules: FeeSchedule[]; monthlyDueDay: number | null }> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/schedules/suggest`, { params: { academicYearId } });
  return data;
}

export async function saveFeeSchedules(institutionId: number, payload: { academicYearId: number; schedules: FeeSchedule[]; monthlyDueDay: number | null; monthlyFirstDue?: string | null; confirmRemoved?: boolean }): Promise<FeeCalendar> {
  const { data } = await privateClient.put(`${feesBase(institutionId)}/schedules`, payload);
  return data;
}

export async function fetchFeeDashboard(institutionId: number, academicYearId?: number): Promise<FeeDashboard> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/dashboard`, { params: academicYearId ? { academicYearId } : undefined });
  return data;
}

export async function refreshInvoiceUnpaidLines(
  institutionId: number,
  payload: { studentId: number; academicYearId: number }
): Promise<{ invoice: StudentFeeInvoice; changed: boolean; refreshed: { added: number; removed: number; replaced: number; kept: number; keptOldPlan?: string[] } }> {
  const { data } = await privateClient.post(FEES.INVOICES.REGENERATE(institutionId), { ...payload, refreshUnpaid: true });
  return data;
}

export async function generateInvoicesForClass(
  institutionId: number,
  payload: { academicGroupId: number; academicYearId: number }
): Promise<GenerateBulkResult> {
  const { data } = await privateClient.post(FEES.INVOICES.GENERATE_BULK(institutionId), payload);
  return data;
}

export async function fetchStudentInvoices(institutionId: number, studentId: number): Promise<StudentFeeInvoice[]> {
  const { data } = await privateClient.get(FEES.INVOICES.BY_STUDENT(institutionId, studentId));
  return data.invoices ?? [];
}

export async function fetchInvoice(institutionId: number, invoiceId: number): Promise<StudentFeeInvoice> {
  const { data } = await privateClient.get(FEES.INVOICES.GET(institutionId, invoiceId));
  return data.invoice;
}

/** A fresh one-time id for one payment attempt: if the same attempt is sent twice (a timeout, then a retry), the server records it once. */
export const newRequestKey = (): string => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);

export async function recordPayment(
  institutionId: number,
  invoiceId: number,
  payload: { amount: number; paymentMode?: PaymentMode; bankAccountId?: number; referenceNo?: string; collectedByName?: string; notes?: string; invoiceItemIds?: number[]; payFullYear?: boolean; requestKey?: string; paidOn?: string }
): Promise<FeePayment> {
  const { data } = await privateClient.post(FEES.PAYMENTS.CREATE(institutionId, invoiceId), payload);
  return data.payment;
}

export async function fetchPayments(institutionId: number, invoiceId: number): Promise<FeePayment[]> {
  const { data } = await privateClient.get(FEES.PAYMENTS.LIST(institutionId, invoiceId));
  return data.payments ?? [];
}

export async function fetchBankAccounts(institutionId: number, params?: { activeOnly?: boolean }): Promise<BankAccount[]> {
  const { data } = await privateClient.get(FEES.BANK_ACCOUNTS.LIST(institutionId), { params });
  return data.accounts ?? [];
}

export async function createBankAccount(
  institutionId: number,
  payload: { type: BankAccountType; label: string; bankName?: string; branch?: string; accountNumber?: string; ifsc?: string; accountHolder?: string; upiId?: string }
): Promise<BankAccount> {
  const { data } = await privateClient.post(FEES.BANK_ACCOUNTS.CREATE(institutionId), payload);
  return data.account;
}

export async function updateBankAccount(
  institutionId: number,
  accountId: number,
  payload: Partial<{ label: string; bankName: string; branch: string; accountNumber: string; ifsc: string; accountHolder: string; upiId: string; isActive: boolean }>
): Promise<BankAccount> {
  const { data } = await privateClient.patch(FEES.BANK_ACCOUNTS.UPDATE(institutionId, accountId), payload);
  return data.account;
}

export async function deleteBankAccount(institutionId: number, accountId: number): Promise<{ deleted?: boolean; account?: BankAccount }> {
  const { data } = await privateClient.delete(FEES.BANK_ACCOUNTS.DELETE(institutionId, accountId));
  return data;
}

export async function fetchFeesOverview(
  institutionId: number,
  params?: { academicYearId?: number }
): Promise<FeesOverviewReport> {
  const { data } = await privateClient.get(FEES.REPORTS.OVERVIEW(institutionId), { params });
  return data.overview;
}

export async function fetchFeesBreakdown(
  institutionId: number,
  params?: { groupBy?: "class" | "date"; academicYearId?: number; from?: string; to?: string }
): Promise<FeesBreakdownRow[]> {
  const { data } = await privateClient.get(FEES.REPORTS.BREAKDOWN(institutionId), { params });
  return data.breakdown ?? [];
}

// --- Discounts / concessions ---------------------------------------------

export async function fetchInvoiceDiscounts(institutionId: number, invoiceId: number): Promise<InvoiceDiscountsResult> {
  const { data } = await privateClient.get(FEES.DISCOUNTS.LIST(institutionId, invoiceId));
  return data;
}

export async function applyInvoiceDiscount(
  institutionId: number,
  invoiceId: number,
  payload: { discountType: DiscountType; value: number; invoiceItemId?: number; reason?: string }
): Promise<InvoiceDiscountsResult> {
  const { data } = await privateClient.post(FEES.DISCOUNTS.CREATE(institutionId, invoiceId), payload);
  return data;
}

export async function revokeInvoiceDiscount(
  institutionId: number,
  invoiceId: number,
  discountId: number
): Promise<InvoiceDiscountsResult> {
  const { data } = await privateClient.delete(FEES.DISCOUNTS.REVOKE(institutionId, invoiceId, discountId));
  return data;
}

// --- Ad-hoc fee charges ----------------------------------------------------

export interface AdhocChargePage {
  charges: AdhocFeeCharge[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export async function fetchAdhocCharges(institutionId: number, params: { academicYearId?: number; page: number; limit: number }): Promise<AdhocChargePage> {
  const { data } = await privateClient.get(FEES.ADHOC_CHARGES.LIST(institutionId), { params });
  return data;
}

export async function createAdhocCharge(
  institutionId: number,
  payload: {
    academicYearId: number;
    feeComponentId: number;
    name: string;
    amount: number;
    dueDate?: string;
    targetScope: AdhocChargeTargetScope;
    // One or more groups — a parent group (e.g. "Class 1") cascades to every
    // descendant group under it, so its sections don't need picking individually.
    academicGroupIds?: number[];
    studentIds?: number[];
    note?: string;
  }
): Promise<AdhocFeeChargeResult> {
  const { data } = await privateClient.post(FEES.ADHOC_CHARGES.CREATE(institutionId), payload);
  return data;
}

export async function cancelAdhocCharge(institutionId: number, chargeId: number): Promise<AdhocFeeChargeResult> {
  const { data } = await privateClient.post(FEES.ADHOC_CHARGES.CANCEL(institutionId, chargeId));
  return data;
}

export interface CollectionQuery {
  academicYearId: number;
  academicGroupId?: number;
  status?: CollectionStatus;
  search?: string;
  sort?: CollectionSort;
  page?: number;
  limit?: number;
}

/** One page of the fee collection worklist (filtered, searched, sorted and paged on the server). */
export async function fetchCollection(institutionId: number, q: CollectionQuery, signal?: AbortSignal): Promise<{ page: number; limit: number; rows: CollectionRow[] }> {
  const { data } = await privateClient.get(FEES.COLLECTION(institutionId), { params: q, signal });
  return data;
}

/** Students who have left but still owe fees from earlier years — they are not on the year-by-year collection list. */
export async function fetchFormerStudentDues(institutionId: number, q: { academicYearId?: number | string; search?: string; page?: number; limit?: number }, signal?: AbortSignal): Promise<FormerStudentDues> {
  const { data } = await privateClient.get(FEES.FORMER_STUDENT_DUES(institutionId), { params: q, signal });
  return data;
}

/** Counts for the status chips + total still owed, for the same class/search scope (status filter not applied). */
export async function fetchCollectionCounts(
  institutionId: number,
  q: Pick<CollectionQuery, "academicYearId" | "academicGroupId" | "search">,
  signal?: AbortSignal
): Promise<{ counts: CollectionCounts; pendingAmount: number }> {
  const { data } = await privateClient.get(FEES.COLLECTION_COUNTS(institutionId), { params: q, signal });
  return data;
}


// ── Concessions, fine waivers, full-year payment, fee policy, fee heads ──
const feesBase = (institutionId: number) => `/institutions/${institutionId}/fees`;

export async function fetchConcessionOffer(institutionId: number): Promise<{ templates: ConcessionTemplate[]; types: ConcessionType[]; allowCustom: boolean }> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/concessions/offer`);
  return data;
}

export interface ConcessionTypeInput {
  id?: number;
  templateCode?: string;
  name: string;
  valueType: ConcessionValueType;
  value: number;
  headScope: "ALL" | "SELECTED";
  headIds?: number[];
  requiresReason?: boolean;
  requiresStaffLink?: boolean;
  isActive?: boolean;
}

export async function saveConcessionType(institutionId: number, payload: ConcessionTypeInput): Promise<ConcessionType> {
  const { data } = await privateClient.post(`${feesBase(institutionId)}/concessions/types`, payload);
  return data.type;
}

export async function fetchStaffForConcession(institutionId: number, q: string): Promise<{ id: number; name: string; email: string | null; phone: string | null }[]> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/concessions/staff`, { params: { q } });
  return data.staff ?? [];
}

export async function fetchStudentConcessions(institutionId: number, studentId: number): Promise<StudentConcession[]> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/students/${studentId}/concessions`);
  return data.concessions ?? [];
}

export async function grantStudentConcession(
  institutionId: number,
  studentId: number,
  payload: { typeId: number; valueType?: ConcessionValueType; value?: number; headScope?: "ALL" | "SELECTED"; headIds?: number[]; academicYearId?: number | null; staffUserId?: number; reason?: string }
): Promise<{ concession: StudentConcession; appliedToExistingInvoices: number }> {
  const { data } = await privateClient.post(`${feesBase(institutionId)}/students/${studentId}/concessions`, payload);
  return data;
}

export async function revokeStudentConcession(institutionId: number, concessionId: number, reason: string): Promise<StudentConcession> {
  const { data } = await privateClient.post(`${feesBase(institutionId)}/concessions/${concessionId}/revoke`, { reason });
  return data.concession;
}

export async function fetchFullYearQuote(institutionId: number, invoiceId: number): Promise<FullYearQuote> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/invoices/${invoiceId}/full-year-quote`);
  return data.quote;
}

export interface FineHistoryEntry { kind: "ADDED" | "WAIVED"; id: number; amount: number; reason: string; at: string; by: string | null; fee: string; stoppedFurther: boolean }

export async function fetchFineHistory(institutionId: number, invoiceId: number): Promise<FineHistoryEntry[]> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/invoices/${invoiceId}/fine-history`);
  return data.history;
}

export async function sendReceipt(institutionId: number, paymentId: number): Promise<{ sent: boolean; channel: "WHATSAPP" | "PUSH" | "NONE"; whatsappError?: string }> {
  const { data } = await privateClient.post(`${feesBase(institutionId)}/payments/${paymentId}/send-receipt`);
  return data;
}

export async function fetchReceiptShare(institutionId: number, paymentId: number): Promise<{ message: string; mobile: string | null }> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/payments/${paymentId}/receipt-share`);
  return data;
}


export async function addInvoiceFine(
  institutionId: number,
  invoiceId: number,
  payload: { invoiceItemId: number; amount: number; reason: string }
): Promise<{ added: number }> {
  const { data } = await privateClient.post(`${feesBase(institutionId)}/invoices/${invoiceId}/add-fine`, payload);
  return data;
}

export async function waiveInvoiceFine(
  institutionId: number,
  invoiceId: number,
  payload: { reason: string; amount?: number; stopFurther?: boolean; invoiceItemIds?: number[] }
): Promise<{ waived: number }> {
  const { data } = await privateClient.post(`${feesBase(institutionId)}/invoices/${invoiceId}/waive-fine`, payload);
  return data;
}

export async function fetchFeePolicy(institutionId: number): Promise<FeePolicy> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/policy`);
  return data.policy;
}

export async function saveFeePolicy(institutionId: number, payload: Partial<FeePolicy>): Promise<FeePolicy> {
  const { data } = await privateClient.put(`${feesBase(institutionId)}/policy`, payload);
  return data.policy;
}

export async function fetchFeeHeads(institutionId: number): Promise<{ heads: FeeHead[]; unmatched: { id: number; name: string; isActive: boolean }[]; custom: { id: number; name: string; isActive: boolean; collectAt: string; refundable: boolean; billingKind?: BillingKind; chargedTo?: "ALL" | "NEW_ADMISSION" | "RE_ADMISSION"; termNos?: number[] | null }[]; allowCustom: boolean }> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/heads`);
  return data;
}

export async function addFeeHead(institutionId: number, payload: { headId?: number; label?: string; componentId?: number; matchHeadId?: number; collectAt?: "ADMISSION" | "LATER"; refundable?: boolean; billingKind?: BillingKind; chargedTo?: "ALL" | "NEW_ADMISSION" | "RE_ADMISSION" }): Promise<{ componentId: number }> {
  const { data } = await privateClient.post(`${feesBase(institutionId)}/heads`, payload);
  return data;
}

export interface RefundLine { itemId: number; name: string; refundable: boolean; baseLeft: number; fineLeft: number }
export interface PaymentRefunds {
  refunds: { id: number; refundNumber: string; amount: number; kind: "REFUND" | "REVERSAL"; reason: string; createdAt: string }[];
  lines: RefundLine[];
  refundableLeft: number;
}

export async function fetchPaymentRefunds(institutionId: number, paymentId: number): Promise<PaymentRefunds> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/payments/${paymentId}/refunds`);
  return data;
}

export async function refundPayment(
  institutionId: number,
  paymentId: number,
  payload: { amount: number; reason: string; refundMode?: PaymentMode; referenceNo?: string; reverse?: boolean }
): Promise<{ refund: { refundNumber: string; kind: "REFUND" | "REVERSAL" } }> {
  const { data } = await privateClient.post(`${feesBase(institutionId)}/payments/${paymentId}/refunds`, payload);
  return data;
}

export async function correctPayment(
  institutionId: number,
  paymentId: number,
  payload: { amount: number; reason: string; paymentMode: PaymentMode; bankAccountId?: number; referenceNo?: string; paidOn?: string; requestKey: string }
): Promise<{ reversal: { refundNumber: string }; payment: FeePayment }> {
  const { data } = await privateClient.post(`${feesBase(institutionId)}/payments/${paymentId}/correct`, payload);
  return data;
}

export async function clearCheque(institutionId: number, paymentId: number): Promise<void> {
  await privateClient.post(`${feesBase(institutionId)}/payments/${paymentId}/cheque/clear`);
}

export async function bounceCheque(institutionId: number, paymentId: number, reason: string): Promise<void> {
  await privateClient.post(`${feesBase(institutionId)}/payments/${paymentId}/cheque/bounce`, { reason });
}

export interface DayClosing { date: string; expectedCash: number; countedCash: number; difference: number; otherModes: Record<string, number>; note: string | null }
export interface DayClosingState { date: string; totals: { expectedCash: number; otherModes: Record<string, number>; receipts: number }; closing: DayClosing | null; recent: DayClosing[] }

export async function fetchDayClosing(institutionId: number, date?: string): Promise<DayClosingState> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/day-closing`, { params: { date } });
  return data;
}
export async function closeFeeDay(institutionId: number, payload: { date?: string; countedCash: number; note?: string }): Promise<DayClosingState> {
  const { data } = await privateClient.post(`${feesBase(institutionId)}/day-closing`, payload);
  return data;
}
export async function reopenFeeDay(institutionId: number, date: string, reason: string): Promise<DayClosingState> {
  const { data } = await privateClient.delete(`${feesBase(institutionId)}/day-closing/${date}`, { data: { reason } });
  return data;
}

export type BillingKind = "ONE_TIME" | "MONTHLY" | "YEARLY" | "TERM";

export async function fetchBillingKinds(institutionId: number): Promise<BillingKind[] | null> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/billing-kinds`);
  return data.billingKinds ?? null;
}

export async function fetchTermFeeCreated(institutionId: number): Promise<boolean> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/billing-kinds`);
  return !!data.termFeeCreated;
}

export async function markTermFeeCreated(institutionId: number): Promise<void> {
  await privateClient.put(`${feesBase(institutionId)}/billing-kinds/term-fee`);
}

export async function saveBillingKinds(institutionId: number, billingKinds: BillingKind[]): Promise<BillingKind[]> {
  const { data } = await privateClient.put(`${feesBase(institutionId)}/billing-kinds`, { billingKinds });
  return data.billingKinds;
}

export interface InstitutionPayment extends FeePayment {
  /** A receipt taken against an old due: it has no invoice and opens at /fee-receipt/old/:id. */
  oldDue?: boolean;
  studentId: number;
  studentName: string;
  studentCode: string | null;
  className: string | null;
  academicYear: string | null;
}
export interface InstitutionPaymentsList {
  payments: InstitutionPayment[];
  totals: { count: number; amount: number; fine: number };
  pagination: { page: number; limit: number; total: number; totalPages: number };
}
export interface InstitutionPaymentsQuery {
  search?: string; from?: string; to?: string; paymentMode?: string; clearanceStatus?: string; academicYearId?: number; academicGroupId?: number; page: number; limit: number;
}

export async function fetchInstitutionPayments(institutionId: number, params: InstitutionPaymentsQuery): Promise<InstitutionPaymentsList> {
  const { data } = await privateClient.get(`${feesBase(institutionId)}/payments`, { params });
  return data;
}
